import React, { useState, useEffect } from 'react';
import { 
  Calculator, 
  TrendingUp, 
  Users, 
  Zap, 
  Shield, 
  CheckCircle, 
  AlertTriangle,
  DollarSign,
  Target,
  BarChart3,
  Clock,
  Sparkles,
  ArrowRight,
  Building,
  Rocket,
  Trophy,
  X
} from 'lucide-react';

interface PricingTier {
  name: string;
  tagline: string;
  price: number;
  description: string;
  features: string[];
  employeeRange: string;
  targetAudience: string[];
  deliverables: string[];
  value: number; // What it would cost traditionally
  successRate: string; // Success probability increase range
  highlighted?: boolean;
  icon: React.ReactNode;
  badgeText?: string;
}

interface ROIInputs {
  transformationScale?: 'foundation' | 'enablement' | 'transformation';
  aiTeamSize: number;
  teamLocation: 'high' | 'medium' | 'low';
  currentMaturity: 'low' | 'medium' | 'high';
  timelineMonths: number;
}

interface ROIResults {
  recommendedPackage: string;
  neuPathCost: number;
  
  // AI Team Investment at Risk
  aiTeamCost: number;
  aiTeamWaste: number;
  
  // Organizational Impact
  organizationalEmployees: number;
  organizationalImpactCost: number;
  
  // Total Impact
  totalCostOfFailure: number;
  successRateImprovement: number;
  
  // ROI Metrics
  totalValueProtected: number;
  roiMultiple: number;
}

export const PricingROICalculator: React.FC = () => {
  const [selectedTier, setSelectedTier] = useState<number>(1);
  const [expandedTiers, setExpandedTiers] = useState<Set<number>>(new Set());
  const [showCalculator, setShowCalculator] = useState(true);
  const [inputs, setInputs] = useState<ROIInputs>({
    transformationScale: undefined,
    aiTeamSize: 5,
    teamLocation: 'medium',
    currentMaturity: 'low',
    timelineMonths: 12
  });
  const [results, setResults] = useState<ROIResults | null>(null);

  // Updated pricing tiers based on document
  const pricingTiers: PricingTier[] = [
    {
      name: "Foundation",
      tagline: "The Status Quo Buster",
      price: 10000,
      description: "Perfect for rapid insights and self-guided change with AI support",
      employeeRange: "Up to 50 employees",
      targetAudience: [
        "Single department or pilot initiatives",
        "Leaders needing data-driven readiness snapshot",
        "Companies testing AI adoption waters"
      ],
      features: [
        "Core Assessment:",
        "- I AM Assessment (1 access code)",
        "- Individual & team readiness scores",
        "- AI-powered insights & executive summary",
        "Platform Access:",
        "- 30-day platform & AI Change Coach",
        "- Department heatmaps & risk zones",
        "Expert Support:",
        "- 1-hour executive debrief",
        "- Recommendations & next steps from our change professionals",
        "- 30 days email support"
      ],
      deliverables: [
        "Baseline readiness score",
        "Success probability calculation",
        "Risk assessment by profile type",
        "Quick wins identification",
        "Critical risk mitigation strategies"
      ],
      value: 40000, // What boutique firm would charge
      successRate: '25-35',
      icon: <Building className="w-8 h-8" />
    },
    {
      name: "Enablement",
      tagline: "Expanded Intelligent Change Platform Access",
      price: 25000,
      description: "Transform insights into action with structured support",
      employeeRange: "50-150 employees",
      targetAudience: [
        "Multi-department initiatives",
        "Companies ready to translate insights to action",
        "Teams needing structured implementation support"
      ],
      features: [
        "Everything in Foundation, plus:",
        "Expanded Assessment:",
        "- 3 access codes for team segmentation",
        "- 3 Assessment Leads with full access",
        "Intelligent Change Platform:",
        "- <green>Action Plan Builder (Beta)</green>",
        "- Department strategies & comparisons",
        "- 60-day platform access",
        "Expert Training & Support:",
        "- 3 facilitated sessions (6 hours total)",
        "- Participant workbooks & tools",
        "- 2 months advisory support"
      ],
      deliverables: [
        "Custom AI adoption roadmap",
        "Milestone-based journey tracker",
        "Resource allocation recommendations",
        "Department-specific strategies",
        "Change velocity projections",
        "Resistance management playbook"
      ],
      value: 125000, // What mid-tier consultant would charge
      successRate: '45-55',
      highlighted: true,
      badgeText: "MOST POPULAR",
      icon: <Rocket className="w-8 h-8" />
    },
    {
      name: "Transformation",
      tagline: "The Enterprise Game-Changer",
      price: 50000,
      description: "White-glove service for company-wide transformation",
      employeeRange: "150-300 employees (for organizations > 300 please contact us for a custom quote via the button below)",
      targetAudience: [
        "Enterprise organizations",
        "Company-wide AI transformations",
        "Multiple concurrent initiatives",
        "Organizations requiring deep strategic support"
      ],
      features: [
        "Everything in Enablement, plus:",
        "Enterprise Assessment:",
        "- 10 access codes & 10 Assessment Leads",
        "- 3 Super Assessment Lead accounts for cross functional analysis",
        "- Customizable for your initiative",
        "Advanced Platform:",
        "- <green>NeuPath Communicate (Beta)</green>",
        "- AI-powered comms planning",
        "- Department-specific action plans",
        "- 90-day platform access",
        "Strategic Consulting (40 hours):",
        "- Executive strategy workshop",
        "- Board presentation development",
        "- Implementation guides & coaching",
        "Dedicated Advisor:",
        "- 3 months advisory support",
        "- On-demand strategic guidance"
      ],
      deliverables: [
        "Comprehensive change architecture",
        "Persona-based playbooks (customized per function)",
        "Stakeholder engagement matrix",
        "Governance structure recommendations",
        "Board-ready progress reports",
        "Success story development",
        "Media kit for announcements"
      ],
      value: 350000, // What enterprise consultants charge
      successRate: '60-70',
      icon: <Trophy className="w-8 h-8" />
    }
  ];

  // Calculate ROI based on inputs
  const calculateROI = () => {
    // Determine recommended package and cost based on transformation scale
    let recommendedPackage: string;
    let neuPathCost: number;
    let baseSuccessImprovement: number;
    let employeesInvolved: number;
    
    switch (inputs.transformationScale) {
      case 'foundation':
        recommendedPackage = 'Foundation Package';
        neuPathCost = 10000;
        baseSuccessImprovement = 30; // 25-35% midpoint
        employeesInvolved = 50;
        break;
      case 'enablement':
        recommendedPackage = 'Enablement Package';
        neuPathCost = 25000;
        baseSuccessImprovement = 50; // 45-55% midpoint
        employeesInvolved = 150;  // Fixed: was 200, should be 150
        break;
      case 'transformation':
        recommendedPackage = 'Transformation Package';
        neuPathCost = 50000;
        baseSuccessImprovement = 65; // 60-70% midpoint
        employeesInvolved = 300;  // Fixed: was 500, should be 300
        break;
      default:
        return; // No scale selected
    }
    
    // Baseline failure rates by maturity
    const baseFailureRates = {
      low: 0.70,    // 70% failure rate (30% success)
      medium: 0.55,  // 55% failure rate (45% success)
      high: 0.40     // 40% failure rate (60% success)
    };
    
    // Direct success rate improvement (no multiplier - maturity already factored into base rates)
    // Cap total success rate at 95%
    const currentSuccessRate = (1 - baseFailureRates[inputs.currentMaturity]) * 100;
    const maxPossibleImprovement = 95 - currentSuccessRate;
    const successRateImprovement = Math.min(baseSuccessImprovement, maxPossibleImprovement);
    
    const baseFailureRate = baseFailureRates[inputs.currentMaturity];
    
    // Calculate AI team investment at risk
    const salaryByLocation = {
      high: 250000,
      medium: 180000,
      low: 120000
    };
    
    const annualSalary = salaryByLocation[inputs.teamLocation];
    const monthlyTeamCost = (inputs.aiTeamSize * annualSalary) / 12;
    const aiTeamCost = monthlyTeamCost * inputs.timelineMonths;  // Fixed: removed multiplication by initiatives
    
    // AI team waste based on failure rate
    const aiTeamWaste = aiTeamCost * baseFailureRate;
    
    // Calculate organizational impact
    const organizationalEmployees = Math.max(employeesInvolved - inputs.aiTeamSize, 0);
    
    // Productivity loss is a fixed percentage (5%) regardless of number of initiatives
    const productivityLoss = 0.05; // Fixed 5% productivity loss
    const avgEmployeeValue = 100000;
    const organizationalImpactCost = organizationalEmployees * avgEmployeeValue * productivityLoss;
    
    // Total cost of failure
    const totalCostOfFailure = aiTeamWaste + organizationalImpactCost;
    
    // Value protected by improving success rate
    const improvedFailureRate = Math.max(0, baseFailureRate - (successRateImprovement / 100));
    const reducedWaste = aiTeamCost * improvedFailureRate;
    const totalValueProtected = aiTeamWaste - reducedWaste + organizationalImpactCost;
    
    // ROI calculation
    const roiMultiple = totalValueProtected / neuPathCost;
    
    setResults({
      recommendedPackage,
      neuPathCost,
      aiTeamCost,
      aiTeamWaste,
      organizationalEmployees,
      organizationalImpactCost,
      totalCostOfFailure,
      successRateImprovement,
      totalValueProtected,
      roiMultiple
    });
  };

  useEffect(() => {
    if (showCalculator && inputs.transformationScale && inputs.aiTeamSize > 0) {
      calculateROI();
    }
  }, [inputs, showCalculator]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pt-24 pb-20">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Transform with Confidence: AI-Powered Change Acceleration 
            <span className="text-teal-600 block">Save 75-85% vs Traditional Consulting</span>
          </h1>
          <p className="text-xl text-gray-700 mb-8">
            Join the top few organizations that succeed at AI transformation. 
            Get enterprise-grade insights at startup-friendly prices.
          </p>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
              <CheckCircle className="text-green-500" size={20} />
              <span className="font-semibold">Average 20:1 ROI</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
              <Clock className="text-blue-500" size={20} />
              <span className="font-semibold">48-Hour Insights</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md">
              <Users className="text-purple-500" size={20} />
              <span className="font-semibold">Personalized for Every Role</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                tier.highlighted ? 'ring-2 ring-teal-500 transform scale-105' : ''
              } ${selectedTier === index ? 'ring-4 ring-teal-300' : ''} hover:shadow-xl transition-all`}
            >
              {tier.badgeText && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                    {tier.badgeText}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-4">
                <div className="text-teal-600">{tier.icon}</div>
                <span className={`text-sm text-right ${tier.name === 'Enablement' ? 'text-teal-600 font-bold' : 'text-gray-500 font-medium'}`}>
                  {tier.tagline}
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{tier.name}</h3>
              <p className="text-gray-600 mb-4">
                {tier.description.split(/<green>|<\/green>/).map((part, idx) => 
                  idx % 2 === 1 ? (
                    <span key={idx} className="text-teal-600 font-semibold">{part}</span>
                  ) : (
                    <span key={idx}>{part}</span>
                  )
                )}
              </p>
              
              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-slate-900">{formatCurrency(tier.price)}</span>
                </div>
                <div className="mt-2">
                  <span className="text-sm text-gray-500 line-through">
                    Traditional cost: {formatCurrency(tier.value)}
                  </span>
                  <span className="text-sm text-green-600 font-semibold ml-2">
                    Save {Math.round(((tier.value - tier.price) / tier.value) * 100)}%
                  </span>
                </div>
              </div>

              <div className="border-t pt-4 mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-2">{tier.employeeRange}</p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <TrendingUp className="text-green-500" size={16} />
                  <span>Success probability increase: {tier.successRate}%</span>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                {(expandedTiers.has(index) ? tier.features : tier.features.slice(0, 5)).map((feature, idx) => {
                  const isGreen = feature.includes('<green>');
                  const cleanFeature = feature.replace(/<green>|<\/green>/g, '');
                  const isSubItem = cleanFeature.startsWith('- ');
                  const isGroupHeader = cleanFeature.endsWith(':') && !cleanFeature.startsWith('- ');
                  const displayFeature = isSubItem ? cleanFeature.substring(2) : cleanFeature;
                  
                  return (
                    <div key={idx} className={`flex items-start gap-2 ${isSubItem ? 'ml-6' : ''} ${isGroupHeader ? 'font-semibold text-gray-800 mt-2' : ''}`}>
                      {!isGroupHeader && <CheckCircle className="text-teal-500 flex-shrink-0 mt-0.5" size={16} />}
                      <span className={`text-sm ${isGreen ? 'text-teal-600 font-semibold' : isGroupHeader ? '' : 'text-gray-700'}`}>
                        {displayFeature}
                      </span>
                    </div>
                  );
                })}
                {tier.features.length > 5 && (
                  <button 
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                    onClick={() => {
                      const newExpanded = new Set(expandedTiers);
                      if (expandedTiers.has(index)) {
                        newExpanded.delete(index);
                      } else {
                        newExpanded.add(index);
                      }
                      setExpandedTiers(newExpanded);
                    }}
                  >
                    {expandedTiers.has(index) ? '− Show less' : `+ ${tier.features.length - 5} more features`}
                  </button>
                )}
              </div>

              <button 
                onClick={() => {
                  if (index === 2) {
                    // Transformation - go to contact form with Executive Briefing
                    window.location.hash = 'contact?interest=Executive%20Briefing';
                    // Force a hash change event if hash didn't change
                    setTimeout(() => {
                      if (!window.location.hash.includes('interest=')) {
                        window.location.hash = 'contact?interest=Executive%20Briefing';
                      }
                    }, 100);
                  } else {
                    // Foundation and Enablement - scroll to ROI calculator and ensure it's open
                    setSelectedTier(index);
                    setShowCalculator(true);
                    // Set default inputs based on tier
                    if (index === 0) {
                      setInputs({
                        ...inputs,
                        transformationScale: 'foundation',
                        aiTeamSize: 5
                      });
                    } else if (index === 1) {
                      setInputs({
                        ...inputs,
                        transformationScale: 'enablement',
                        aiTeamSize: 10
                      });
                    }
                    setTimeout(() => {
                      const calculatorSection = document.getElementById('roi-calculator');
                      calculatorSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }
                }}
                className={`w-full py-3 px-6 rounded-full font-semibold transition-all ${
                  selectedTier === index
                    ? 'bg-teal-700 text-white hover:bg-teal-800 ring-2 ring-teal-300' 
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}>
                {index === 2 ? 'Request Executive Briefing' : 'Calculate ROI'}
              </button>
            </div>
          ))}
        </div>
        
        {/* Features Disclaimer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 italic max-w-3xl mx-auto">
            * We're continuously improving our platform. Features marked as "Beta" are in active development 
            and we're committed to delivering them, though functionality may evolve based on user feedback. 
            Our goal is to exceed your expectations as we refine and enhance these capabilities. See our{' '}
            <button 
              onClick={() => window.location.hash = 'terms'}
              className="text-teal-600 hover:text-teal-700 underline"
            >
              Terms of Service
            </button> for full details.
          </p>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section id="roi-calculator" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Calculate Your ROI
            </h2>
            <p className="text-lg text-gray-700">
              Understand the cost of AI transformation failure and how NeuPath reduces your risk
            </p>
          </div>

          {showCalculator && (
            <div className="space-y-8">
              {/* Calculator Inputs */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Your AI Transformation Profile</h3>
                
                {/* Step 1: Transformation Scale & Maturity */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-teal-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm mr-2">1</span>
                    Select Your Scale & Current State
                  </h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Transformation Scale
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Choose based on your initiative scope</p>
                      <div className="space-y-2">
                        <button
                          onClick={() => setInputs({
                            ...inputs, 
                            transformationScale: 'foundation',
                                aiTeamSize: 5
                          })}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                            inputs.transformationScale === 'foundation' 
                              ? 'border-teal-500 bg-teal-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-semibold text-gray-900">Foundation</p>
                          <p className="text-xs text-gray-600">1-3 AI initiatives, pilot teams</p>
                        </button>
                        <button
                          onClick={() => setInputs({
                            ...inputs, 
                            transformationScale: 'enablement',
                                aiTeamSize: 10
                          })}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                            inputs.transformationScale === 'enablement' 
                              ? 'border-teal-500 bg-teal-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-semibold text-gray-900">Enablement</p>
                          <p className="text-xs text-gray-600">3-5 AI initiatives, department-wide</p>
                        </button>
                        <button
                          onClick={() => setInputs({
                            ...inputs, 
                            transformationScale: 'transformation',
                            aiTeamSize: 20
                          })}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                            inputs.transformationScale === 'transformation' 
                              ? 'border-teal-500 bg-teal-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-semibold text-gray-900">Transformation</p>
                          <p className="text-xs text-gray-600">5+ AI initiatives, enterprise-wide</p>
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current AI Maturity Level
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Your starting point affects potential improvement</p>
                      <select
                        value={inputs.currentMaturity}
                        onChange={(e) => setInputs({...inputs, currentMaturity: e.target.value as any})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="low">Low - Just starting with AI</option>
                        <option value="medium">Medium - Some AI pilots running</option>
                        <option value="high">High - Multiple AI initiatives underway</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Step 2: Initiative Details */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-teal-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm mr-2">2</span>
                    Define Your AI Investment
                  </h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI Team Size
                      </label>
                      <input
                        type="number"
                        value={inputs.aiTeamSize}
                        onChange={(e) => {
                          const value = Math.max(0, parseInt(e.target.value) || 0);
                          setInputs({...inputs, aiTeamSize: value});
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        min="0"
                        placeholder="Number of AI specialists"
                      />
                      <p className="text-xs text-gray-500 mt-1">Data scientists, ML engineers, AI architects</p>
                    </div>
                  </div>
                </div>

                {/* Step 3: Team & Timeline */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="bg-teal-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm mr-2">3</span>
                    Team Location & Timeline
                  </h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Team Location / Salary Level
                      </label>
                      <select
                        value={inputs.teamLocation}
                        onChange={(e) => setInputs({...inputs, teamLocation: e.target.value as any})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="high">High Cost (SF, NYC, London) - $250k/yr</option>
                        <option value="medium">Medium Cost (Austin, Chicago, Toronto) - $180k/yr</option>
                        <option value="low">Lower Cost (Remote, Midwest, Offshore) - $120k/yr</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Implementation Timeline
                      </label>
                      <select
                        value={inputs.timelineMonths}
                        onChange={(e) => setInputs({...inputs, timelineMonths: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value={3}>3 months</option>
                        <option value={6}>6 months</option>
                        <option value={12}>12 months</option>
                        <option value={18}>18 months</option>
                        <option value={24}>24 months</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Display */}
              {results && (
                <div className="space-y-6">
                  {/* Risk Summary */}
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                      <AlertTriangle className="mr-2" size={24} />
                      Your AI Transformation Risk
                    </h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* AI Team Investment at Risk */}
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">AI Team Investment at Risk</p>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{inputs.aiTeamSize} AI specialists × {inputs.timelineMonths} months:</span>
                            <span className="font-semibold">{formatCurrency(results.aiTeamCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Cost of failure:</span>
                            <span className="font-bold text-red-600">{formatCurrency(results.aiTeamWaste)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Organizational Impact */}
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">Organizational Impact</p>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{results.organizationalEmployees} employees involved:</span>
                            <span className="font-semibold">~5% productivity loss</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Productivity & change resistance:</span>
                            <span className="font-bold text-orange-600">{formatCurrency(results.organizationalImpactCost)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-red-200">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-gray-800">Total Cost of Failure:</span>
                        <span className="text-2xl font-bold text-red-600">{formatCurrency(results.totalCostOfFailure)}</span>
                      </div>
                    </div>
                  </div>

                  {/* NeuPath Solution */}
                  <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-teal-800 mb-4 flex items-center">
                      <Shield className="mr-2" size={24} />
                      With NeuPath {results.recommendedPackage}
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Success rate improvement:</span>
                        <span className="font-bold text-teal-600">+{results.successRateImprovement}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Value protected from failure:</span>
                        <span className="font-bold text-teal-600">{formatCurrency(results.totalValueProtected)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Your investment:</span>
                        <span className="font-semibold">{formatCurrency(results.neuPathCost)}</span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-teal-200">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-800">Return on Investment:</span>
                          <span className="text-3xl font-bold text-teal-600">{results.roiMultiple.toFixed(1)}x</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="text-center">
                    <p className="text-lg text-gray-700 mb-2">
                      Every month of delay increases failure risk by 2%
                    </p>
                    <p className="text-sm text-gray-600 mb-6">
                      That's {formatCurrency(results.totalCostOfFailure * 0.02)} in additional risk per month
                    </p>
                    <button 
                      onClick={() => {
                        if (inputs.transformationScale === 'transformation') {
                          window.location.hash = 'contact?interest=Executive%20Briefing';
                        } else {
                          window.location.hash = 'contact?interest=' + encodeURIComponent(results.recommendedPackage);
                        }
                        // Ensure the hash change is preserved
                        setTimeout(() => {
                          if (!window.location.hash.includes('interest=')) {
                            const interest = inputs.transformationScale === 'transformation' 
                              ? 'Executive%20Briefing' 
                              : encodeURIComponent(results.recommendedPackage);
                            window.location.hash = 'contact?interest=' + interest;
                          }
                        }, 100);
                      }}
                      className="bg-teal-600 text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-teal-700 transition transform hover:-translate-y-0.5 inline-flex items-center"
                    >
                      Get Started with {results.recommendedPackage} <ArrowRight className="ml-2" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Value Comparison Table */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
          How We Compare
        </h2>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Big 4 Consulting</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Boutique Firms</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-teal-600">NeuPath AI Platform</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Time to Insights</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">6-12 weeks</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">4-8 weeks</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-teal-600">48-72 hours</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Total Project Cost</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">$75,000-$300,000</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">$40,000-$150,000</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-teal-600">$10,000-$50,000</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Personalization</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Generic personas</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Basic segmentation</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-teal-600">Individual AI insights</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Ongoing Support</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Hourly billing</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Retainer required</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-teal-600">Included in package</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Success Tracking</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Quarterly reviews</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">Monthly check-ins</td>
                  <td className="px-6 py-4 text-center text-sm font-semibold text-teal-600">Real-time dashboard</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>


      {/* Urgency Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-12">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
          <AlertTriangle className="text-red-500 mx-auto mb-4" size={48} />
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            The Cost of Waiting
          </h3>
          <p className="text-lg text-gray-700 mb-6">
            Transformation failure rate increases 2% each month of delay
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4">
              <p className="text-3xl font-bold text-red-600">70%</p>
              <p className="text-sm text-gray-600">Current failure rate</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-3xl font-bold text-orange-600">$75,000</p>
              <p className="text-sm text-gray-600">Daily risk exposure</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};